import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Globe, Layout, Puzzle, Lightbulb } from "lucide-react";

const items = [
  {
    icon: Globe,
    title: "Sitios web profesionales",
    description: "Para tu empresa o startup, diseñados a medida con las mejores tecnologías.",
    span: "md:col-span-2",
  },
  {
    icon: Layout,
    title: "Aplicaciones web",
    description: "Para gestionar procesos internos: proyectos, ventas, inventarios y más.",
    span: "md:col-span-1",
  },
  {
    icon: Puzzle,
    title: "Módulos e integraciones",
    description: "Herramientas a medida que se conecten con tus sistemas actuales.",
    span: "md:col-span-1",
  },
  {
    icon: Lightbulb,
    title: "Prototipos funcionales",
    description: "Para validar una idea de producto digital antes de invertir a gran escala.",
    span: "md:col-span-2",
  },
];

const PromoSection = ({ onApplyClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="promo"
      data-testid="promo-section"
      ref={ref}
      className="relative py-24 sm:py-32 noise-overlay"
      style={{ backgroundColor: "#050505" }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-xs font-mono tracking-[0.25em] uppercase text-neutral-500 block mb-4">
            Qué incluye
          </span>
          <h2
            data-testid="promo-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white max-w-3xl"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            ¿Qué incluye el desarrollo de software completamente gratis?
          </h2>
          <p className="text-base sm:text-lg text-neutral-400 leading-relaxed mt-6 max-w-2xl">
            Durante esta campaña, Zulunity ofrece el desarrollo de soluciones de software
            a medida sin costo de desarrollo para un número limitado de proyectos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * (i + 1) }}
              className={`group bg-[#0A0A0A] border border-white/[0.06] hover:border-white/20 p-8 sm:p-10 transition-all duration-300 ${item.span}`}
            >
              <item.icon className="w-6 h-6 text-white mb-6 opacity-60 group-hover:opacity-100 transition-opacity" />
              <h3 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
                {item.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-8 text-sm text-neutral-500 leading-relaxed max-w-3xl border-l border-white/10 pl-6"
        >
          Cada proyecto se diseña contigo, según tus objetivos de negocio, procesos y
          contexto actual. Nos interesa construir soluciones reales que tu equipo pueda
          usar y que agreguen valor desde el día uno.
        </motion.p>
      </div>
    </section>
  );
};

export default PromoSection;
